import { useTraits } from '@nuxt-common/media-card-large/app/composables/useTraits';
import { useInitColorPDP as useInitColor, useColor as useColorMCL } from '@nuxt-common/media-card-large/app/modules/color';
import { injectLocal, provideLocal } from '@vueuse/core';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductType } from '~/composables/useProductType';
import useConsolidatedLatchkey from '~/modules/latchkey/composables/useConsolidatedLatchkey';
import { usePageData } from '~/composables/usePageData';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useConsolidatedMember } from '~/composables/useConsolidatedMember';
import useIsolatedStickyAtbStateStore from '~/modules/sticky-product-traits/stores/useIsolatedStickyAtbStateStore';

import type { ColorProps, Color, SelectedColorId } from '~/declaration/traits';

/**
 * TODO: Traits initialization should be moved to the Product level
 * @description
 * This composable is responsible for providing the color data for the Product.
 * But it also handles complicated case when the Product is a Consolidated Latchkey.
 */
const COLORS_TRAITS_REPOSITORY = 'colors-traits';

interface ColorData {
  selectedColorId: SelectedColorId;
  selectedColor: import('vue').WritableComputedRef<Color | null>;
  colorProps: import('vue').ComputedRef<ColorProps>;
  colors: import('vue').ComputedRef<Color[]>;
}

function getData(productId: string): ColorData {
  const stickyAtbStore = useIsolatedStickyAtbStateStore();
  const { isConsolidatedLatchKey, isShopTheSet } = useProductType();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();

  if (isShopTheSetExpEnabled && isShopTheSet) {
    const { colors, colorProps, selectedColorId, selectedColor } = useConsolidatedMember();
    return { colors, colorProps, selectedColorId, selectedColor };
  }

  if (isConsolidatedLatchKey) {
    const { colors, colorProps, selectedColorId, selectedColor } = useConsolidatedLatchkey();

    return {
      colors,
      colorProps,
      selectedColorId,
      selectedColor,
    };
  }

  const { traits } = usePageData();
  const { colors } = useColorMCL(productId);
  const colorProps = useInitColor({
    id: productId,
    data: (traits.color[productId] ??
      (stickyAtbStore.pageDataStickyAtbTraits as unknown as Record<string, Record<string, unknown>> | null)?.color?.[
        productId
      ]) as unknown as Parameters<typeof useInitColor>[0]['data'],
  });
  const { selectedColorId, selectedColor } = useTraits(productId);

  return {
    colors,
    colorProps,
    selectedColorId,
    selectedColor,
  };
}

export default function useColor(): ColorData {
  const { productId } = useProduct();
  const resultRepository = `${COLORS_TRAITS_REPOSITORY}-${productId}`;
  const providedContext = injectLocal(resultRepository, null);

  if (providedContext) {
    return providedContext;
  }

  const toProvide = getData(productId);
  provideLocal(resultRepository, toProvide);
  return toProvide;
}
